import type { AleResult, Phase, Timeline } from '@val/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchResult, fetchTimeline } from '../lib/api.ts'
import { POLL_INTERVAL_MS } from '../lib/config.ts'

interface State {
  result: AleResult | null
  timeline: Timeline | null
  error: string | null
  loading: boolean
  lastUpdated: number | null
}

export function useAleResult(phase?: Phase, poll = true) {
  const [state, setState] = useState<State>({
    result: null,
    timeline: null,
    error: null,
    loading: true,
    lastUpdated: null,
  })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  const load = useCallback(async () => {
    try {
      const [result, timeline] = await Promise.all([fetchResult(phase), fetchTimeline(phase)])
      if (!alive.current) return
      setState({ result, timeline, error: null, loading: false, lastUpdated: Date.now() })
    } catch (err) {
      if (!alive.current) return
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : 'Kunde inte hämta resultat',
        loading: false,
      }))
    }
  }, [phase])

  useEffect(() => {
    alive.current = true
    setState((s) => ({ ...s, loading: true }))
    load()

    if (poll) {
      const tick = () => {
        timer.current = setTimeout(async () => {
          if (document.visibilityState === 'visible') await load()
          tick()
        }, POLL_INTERVAL_MS)
      }
      tick()
    }

    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [load, poll])

  return { ...state, reload: load }
}
